import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import AlreadyClaimedRewardPopup from "../../components/AlreadyClaimedRewardPopup/AlreadyClaimedRewardPopup";
import FirebaseKey from "../../components/FirebaseKey/FirebaseKey";
import LoginAlertPopup from "../../components/LoginAlertPopup/LoginAlertPopup";
import LoginPopup from "../../components/LoginPopup/LoginPopup";
import Menu from "../../components/Menu/Menu";
import OtpPopup from "../../components/OtpPopup/OtpPopup";
import RewardPopup from "../../components/RewardPopup/RewardPopup";
import TransactionErrorPopup from "../../components/TransactionErrorPopup/TransactionErrorPopup";
import { router } from "../../core/helpers/router";
import { useLoginJourney, useSendOtp } from "../../core/services/auth.service";
import FirebaseService, {
  useRemoteConfig,
} from "../../core/services/firebase.service";
import { useHomeData } from "../../core/services/home.service";
import {
  useConsumeJourney,
  useJourneyData,
} from "../../core/services/journey.service";

const Home = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoading: isConfigLoading } = useRemoteConfig();
  const { data: homeData, isLoading: isDataLoading } = useHomeData();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginAlertVariant, setLoginAlertVariant] = useState<
    "journey" | "game" | undefined
  >();
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [msisdnForOtp, setMsisdnForOtp] = useState<string | null>(null);
  const [showLoginRewardPopup, setShowLoginRewardPopup] = useState(false);
  const [loginRewardData, setLoginRewardData] = useState<{
    rewardAmount: number;
    catalogTempId: string | null;
    isAlreadyClaimed: boolean;
  } | null>(null);
  const [showTransactionErrorPopup, setShowTransactionErrorPopup] =
    useState(false);
  const sendOtp = useSendOtp();
  const loginJourney = useLoginJourney();
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<
    | {
        top: number;
        right: number;
      }
    | undefined
  >(undefined);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const hash = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("hash");
  }, []);

  useEffect(() => {
    if (!hash) {
      setShowLoginPopup(true);
    }
  }, [hash]);

  const { data: journeyData, isLoading: isJourneyLoading } =
    useJourneyData(hash);
  const consumeJourney = useConsumeJourney();

  const isLoading = isConfigLoading || isDataLoading;

  const currentMission = useMemo(() => {
    if (!journeyData?.userLevel || !journeyData?.journeyMissionResponses) {
      return null;
    }
    return journeyData.journeyMissionResponses.find(
      (mission) => mission.level === journeyData.userLevel,
    );
  }, [journeyData]);

  const handleGameClick = (redirectUrl: string) => {
    if (!hash) {
      setLoginAlertVariant("game");
      return;
    }
    const urlWithHash = `${redirectUrl}#${hash}`;
    window.location.href = urlWithHash;
  };

  const handleJourneyButtonClick = async () => {
    if (!hash) {
      setLoginAlertVariant("journey");
      return;
    }

    try {
      const updatedData = await consumeJourney.mutateAsync(hash);

      queryClient.setQueryData(["journey_data"], updatedData);

      const url = `${router.JOURNEY}?hash=${hash}`;
      navigate(url);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      if (message === "transaction_already_used") {
        setShowTransactionErrorPopup(true);
        return;
      }
      console.error("Error consuming journey:", error);
      const url = hash ? `${router.JOURNEY}?hash=${hash}` : router.JOURNEY;
      navigate(url);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-white flex flex-col">
        <div className="flex justify-center items-center min-h-screen">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const recommendedGames = homeData?.recommendedGames || [];
  const games = homeData?.games || [];

  return (
    <div className="min-h-screen w-full bg-white flex flex-col">
      <Menu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        anchorPosition={menuAnchorPosition}
      />
      <TransactionErrorPopup isOpen={showTransactionErrorPopup} />
      <LoginAlertPopup
        isOpen={loginAlertVariant !== undefined}
        onClose={() => setLoginAlertVariant(undefined)}
        variant={loginAlertVariant!}
        onLoginClick={() => {
          setLoginAlertVariant(undefined);
          setShowLoginPopup(true);
        }}
      />
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        onLoginSubmit={async (msisdn) => {
          await sendOtp.mutateAsync(msisdn);
          setShowLoginPopup(false);
          setMsisdnForOtp(msisdn);
          setShowOtpPopup(true);
        }}
      />
      <OtpPopup
        isOpen={showOtpPopup}
        onClose={() => setShowOtpPopup(false)}
        onContinue={async (otp, setError) => {
          if (!msisdnForOtp) return;
          try {
            const data = await loginJourney.mutateAsync({
              msisdn: msisdnForOtp,
              otp,
            });
            queryClient.setQueryData(["journey_data"], data);
            setShowOtpPopup(false);
            if (data.rewardAmount != null && data.rewardAmount > 0) {
              setLoginRewardData({
                rewardAmount: data.rewardAmount,
                catalogTempId: data.catalogTempId,
                isAlreadyClaimed: data.catalogTempId == null,
              });
              setShowLoginRewardPopup(true);
            }
            return true;
          } catch (error) {
            setError(true);
            return false;
          }
        }}
        onResend={() => {
          if (msisdnForOtp) sendOtp.mutate(msisdnForOtp);
        }}
      />
      {showLoginRewardPopup &&
        loginRewardData &&
        (loginRewardData.isAlreadyClaimed ? (
          <AlreadyClaimedRewardPopup
            rewardAmount={loginRewardData.rewardAmount}
            onClose={() => {
              setShowLoginRewardPopup(false);
              setLoginRewardData(null);
            }}
          />
        ) : (
          <RewardPopup
            rewardAmount={loginRewardData.rewardAmount}
            catalogTempId={loginRewardData.catalogTempId}
            deeplink="https://kazandirio.go.link/i4UxA"
            onClose={() => {
              setShowLoginRewardPopup(false);
              setLoginRewardData(null);
            }}
          />
        ))}

      <main className="flex-1 flex flex-col">
        <section className="mb-6">
          <div className="flex items-center justify-between gap-2 mx-6 mb-2 mt-6">
            <h2 className="text-base font-semibold text-black text-left">
              <FirebaseKey firebaseKey={FirebaseService.hub_journey_header} />
            </h2>
            <button
              ref={menuButtonRef}
              className="bg-transparent w-8 h-8 border-none cursor-pointer p-1 flex items-center justify-center transition-opacity hover:opacity-70 active:opacity-50 shadow-xl rounded-full"
              onClick={(e) => {
                if (menuButtonRef.current) {
                  const rect = menuButtonRef.current.getBoundingClientRect();
                  setMenuAnchorPosition({
                    top: rect.bottom + 8,
                    right: window.innerWidth - rect.right,
                  });
                }
                setIsMenuOpen(true);
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.6667 14L21.3334 14M10.6667 18L21.3334 18"
                  stroke="black"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          {/* <div className="mx-6 rounded-[20px] aspect-square grid grid-cols-1 grid-rows-1 overflow-hidden relative">
            <img src={journeybg} alt="Banner" className="col-span-full row-span-full w-full h-full object-cover object-[0%_83%]" />
            <div className="col-span-full row-span-full absolute inset-0 bg-[radial-gradient(circle_at_50%_10%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.8)_100%)] pointer-events-none"></div>
            <div className="flex flex-col justify-end p-6 col-span-full row-span-full relative z-10">

              <h3 className="text-base font-medium text-center text-white mb-1">
                <FirebaseKey firebaseKey={FirebaseService.hub_journey_title} />
              </h3>
              <h4 className="text-sm font-medium text-center text-white/70 mb-2.5">
                <FirebaseKey firebaseKey={FirebaseService.hub_journey_desc} />
              </h4>
              <div id="progress" className="mb-2.5 w-full text-white">
                <ProgressBar journeyData={journeyData} />
              </div>
              <div id="nexttask" className="relative h-14 mt-2">
                <div className="absolute -top-2 inset-x-4 z-10 h-14 rounded-full bg-[#F5F4F2] opacity-70 border border-[rgba(120,119,118,0.20)]">
                </div>
                <div className="absolute inset-0 z-20 h-14 rounded-full bg-white border border-[rgba(120,119,118,0.20)] flex items-center justify-between pl-5 pr-2 gap-2.5">

                  <div className="flex items-center gap-2.5">
                    <span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.8103 8.523L10.7728 20.5605C10.4915 20.8416 10.1101 20.9995 9.71248 20.9995C9.31482 20.9995 8.93344 20.8416 8.65217 20.5605L1.93779 13.8105C1.65699 13.5293 1.49927 13.1481 1.49927 12.7507C1.49927 12.3532 1.65699 11.972 1.93779 11.6908L3.81279 9.81581C4.09391 9.53586 4.47449 9.37869 4.87123 9.37869C5.26797 9.37869 5.64854 9.53586 5.92967 9.81581L9.74998 13.5208L18.8212 4.56112C19.1024 4.28089 19.4832 4.12354 19.8801 4.12354C20.2771 4.12354 20.6579 4.28089 20.939 4.56112L22.8094 6.393C22.95 6.53238 23.0617 6.69823 23.1379 6.88099C23.2141 7.06374 23.2534 7.25979 23.2535 7.4578C23.2536 7.65582 23.2145 7.8519 23.1384 8.03472C23.0623 8.21754 22.9508 8.38349 22.8103 8.523Z" fill="#0088FF" />
                      </svg>
                    </span>
                    <div>
                      <h4 className="text-xs font-medium text-[rgba(67,66,66,0.60)] max-w-[130px] overflow-hidden">
                        <FirebaseKey firebaseKey={FirebaseService.hub_journey_next_task_title} />
                      </h4>
                      <h3 className="text-base font-medium text-black">
                        {currentMission?.title || ""}
                      </h3>
                    </div>

                  </div>
                  <div className="flex items-center gap-2 py-2">
                    <Button
                      className="rounded-full  bg-[#0088FF] hover:bg-[#0088FF]/90 text-white py-2.5 px-4 h-auto"
                      onClick={handleJourneyButtonClick}
                      disabled={consumeJourney.isPending}
                    >
                      {currentMission?.isMilestone && (
                        <span className="text-sm font-medium mr-1">
                          {currentMission.rewardAmount / 1000} GB
                        </span>
                      )}
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.3335 10H16.6669M16.6669 10L11.6668 5M16.6669 10L11.6668 15" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>

                    </Button>
                  </div>
                </div>
              </div>
            </div>

          </div> */}
        </section>

        {recommendedGames.length > 0 && (
          <section className="mb-12 mx-auto w-full">
            <h2 className="text-base font-semibold text-black mb-2 mx-6 text-left">
              <FirebaseKey
                firebaseKey={FirebaseService.recommended_games_title}
              />
            </h2>
            <div className="flex gap-3 overflow-x-auto px-6 snap-x snap-mandatory [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {recommendedGames.map((game) => (
                <div
                  key={game.id}
                  className="flex-none w-[80px] aspect-square snap-start scroll-mx-6 cursor-pointer transition-transform duration-200 active:scale-95"
                  onClick={() => handleGameClick(game.redirectUrl)}
                >
                  <div
                    className="w-full h-full rounded-[20px] overflow-hidden relative bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${game.imageUrl})` }}
                  ></div>
                </div>
              ))}
            </div>
          </section>
        )}

        {games.length > 0 && (
          <section className="pb-8  mx-auto w-full">
            <h2 className="text-base font-semibold text-black mb-2 mx-6 text-left">
              <FirebaseKey firebaseKey={FirebaseService.games_title} />
            </h2>
            <div className="grid grid-cols-2 gap-2.5 px-6">
              {games.map((game) => (
                <div
                  key={game.id}
                  className="aspect-[171/240] rounded-2xl overflow-hidden cursor-pointer transition-transform duration-200 relative active:scale-[0.98] group"
                  onClick={() => handleGameClick(game.redirectUrl)}
                >
                  <div
                    className="w-full h-full bg-cover bg-center bg-no-repeat relative"
                    style={{ backgroundImage: `url(${game.imageUrl})` }}
                  ></div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default Home;
